import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from 'react';

export const THEME_IDS = ['midnightAthlete'] as const;
export type ThemeId = (typeof THEME_IDS)[number];

export type ThemeAppearance = 'dark';

export const THEME_OPTIONS: {
  id: ThemeId;
  label: string;
  description: string;
  appearance: ThemeAppearance;
}[] = [
  { id: 'midnightAthlete', label: 'Midnight Athlete', description: 'Premium dark athletic', appearance: 'dark' },
];

const STORAGE_KEY = 'preformiq_theme';

/** Locked premium dark identity */
export const DEFAULT_THEME: ThemeId = 'midnightAthlete';

function syncDocumentTheme(id: ThemeId) {
  const root = document.documentElement;
  root.dataset.theme = id;
  root.dataset.appearance = 'dark';
  root.classList.add('dark');
  root.classList.remove('light');

  const meta = document.querySelector('meta[name="color-scheme"]');
  if (meta) meta.setAttribute('content', 'dark light');
}

type ThemeCtx = {
  theme: ThemeId;
  setTheme: (id: ThemeId) => void;
};

const ThemeContext = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = DEFAULT_THEME;

  useLayoutEffect(() => {
    syncDocumentTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, []);

  const setTheme = useCallback(() => {
    /* Midnight Athlete is the locked visual identity */
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const c = useContext(ThemeContext);
  if (!c) throw new Error('useTheme outside ThemeProvider');
  return c;
}
